
import tinycolor from 'tinycolor2';
import api from '@/api';
import mimeTypes from '@/mimeTypes';

const namespaced = true;

const state = {
};

const mutations = {
};

const actions = {
  async fetchList(noop, { queryParams = {} }) {
    const { data: interventions } = await api.get(`/interventions`, {
      params: queryParams,
    });
    return interventions;
  },
  async fetchListForChallengingIntervention(noop, challengingInterventionId) {
    const { data: interventions } = await api.get(`/interventions/challenging/${challengingInterventionId}`);

    return interventions;
  },
  async fetchRawList(noop, { queryParams = {} }) {
    const { data: interventions } = await api.get(`/interventions/raw`, {
      params: queryParams,
    });
    return interventions;
  },
  async fetchOne(noop, id) {
    try {
      const { data: intervention } = await api.get(`/interventions/${id}`);

      return intervention;
    } catch (e) {
      return null;
    }
  },
  async fetchSteps(noop, id) {
    const { data: steps } = await api.get(`/intervention-steps/intervention/${id}`);

    return steps;
  },
  async search(noop, searchText) {
    const { data: interventions } = await api.get(`/interventions`, {
      params: { search: searchText },
    });

    return interventions;
  },
  async addIntervention(noop, intervention) {
    const queryParams = {};
    if (intervention.$skipNotifications) {
      queryParams.skipNotifications = 1;
    }
    const { data: dbIntervention } = await api.post(`/interventions`,
      {
        makersData: [{
          id: intervention.Maker_id,
          Priority_id: intervention.Priority_id || null,
          Domain_id: intervention.Domain_id || null,
          Status_id: intervention.Status_id || null,
        }],
        Site_id: intervention.Site_id || null,
        Assignee_id: intervention.Assignee_id || null,
        Client_id: intervention.Client_id,
        displayNum: intervention.displayNum || ``,
        description: intervention.description,
        Project_id: intervention.Project_id || null,
        plannedStartAt: intervention.plannedStartAt || null,
        plannedEndAt: intervention.plannedEndAt || null,
        plannedDaysDuration: intervention.plannedDaysDuration || null,
        attachments: (intervention.attachments || []).map(attachment => ({
          filename: attachment.file.name,
          mimeType: attachment.file.type,
          content: attachment.fileBase64,
        })),
      },
      {
        params: queryParams,
      }
    );

    return dbIntervention;
  },
  async updateIntervention(noop, intervention) {
    const { data: dbIntervention } = await api.put(`/interventions/${intervention.id}`, {
      Assignee_id: intervention.Assignee_id || null,
      plannedStartAt: intervention.plannedStartAt,
      plannedEndAt: intervention.plannedEndAt,
      plannedDaysDuration: intervention.plannedDaysDuration,
    });

    return dbIntervention;
  },
  async addStep(noop, { interventionId, comment, statusId }) {
    const { data: intervention } = await api.post(`/intervention-steps`, {
      Intervention_id: interventionId,
      description: comment,
      Status_id: statusId,
    });

    return intervention;
  },

  async changeAssignee(noop, { interventionId, assigneeId }) {
    const { data: intervention } = await api.post(`/interventions/${interventionId}/assign`, {
      Intervention_id: interventionId,
      Assignee_id: assigneeId,
    });

    return intervention;
  },
  async addClientTag(store, { interventionId, tagId }) {
    const { data: tag } = await api.post(`/interventions/${interventionId}/client-tags`, {
      Tag_id: tagId,
    });

    return tag;
  },
  removeClientTag(store, { interventionId, tagId }) {
    return api.delete(`/interventions/${interventionId}/client-tags/${tagId}`);
  },
  async addMakerTag(store, { interventionId, tagId }) {
    const { data: tag } = await api.post(`/interventions/${interventionId}/maker-tags`, {
      Tag_id: tagId,
    });

    return tag;
  },
  removeMakerTag(store, { interventionId, tagId }) {
    return api.delete(`/interventions/${interventionId}/maker-tags/${tagId}`);
  },
  linkEquipment(store, { interventionId, equipmentId }) {
    return api.post(`/interventions/${interventionId}/link-equipment`, {
      Equipment_id: equipmentId,
    });
  },
  unlinkEquipment(store, { interventionId, equipmentId }) {
    return api.post(`/interventions/${interventionId}/unlink-equipment`, {
      Equipment_id: equipmentId,
    });
  },
  async close(store, { interventionId, comment }) {
    try {
      await api.post(`/interventions/${interventionId}/close`, { comment });

      return true;
    } catch (e) { // eslint-disable-line
      return false;
    }
  },
  remove(store, interventionId) {
    return api.delete(`/interventions/${interventionId}`);
  },
  updatePlannedDate(store, {
    interventionId, plannedStartAt, plannedEndAt, plannedDaysDuration, isPlannedHourly,
  }) {
    return api.post(`/interventions/${interventionId}/plan`, {
      plannedStartAt,
      plannedEndAt,
      plannedDaysDuration,
      isPlannedHourly,
    });
  },
  async fetchNotes(store, interventionId) {
    const { data: notes } = await api.get(`/intervention-notes/intervention/${interventionId}`);

    return notes;
  },
  async fetchReports(store, interventionId) {
    const { data: reports } = await api.get(`/intervention-reports/intervention/${interventionId}`);

    return reports;
  },
  async fetchReport(store, reportId) {
    const { data: report } = await api.get(`/intervention-reports/${reportId}`);

    return report;
  },
  async addNote(store, { interventionId, note }) {
    try {
      const { data: newNote } = await api.post(`/intervention-notes`, {
        interventionId,
        note,
      });

      return newNote;
    } catch (e) { // eslint-disable-line
      return null;
    }
  },
  async updateNote(store, { id, note }) {
    try {
      const { data: updatedNote } = await api.put(`/intervention-notes/${id}`, { note });

      return updatedNote;
    } catch (e) { // eslint-disable-line
      return null;
    }
  },
};

const getters = {
  statusFontColor: () => status => {
    const luminance = tinycolor(status.color).getLuminance();
    return luminance > 0.50 ? `#000` : `#FFF`;
  },
  colorFromBG: () => color => {
    const luminance = tinycolor(color).getLuminance();
    return luminance > 0.50 ? `#000` : `#FFF`;
  },
  attachmentIcon: () => ({ mimeType }) =>
    mimeTypes[mimeType] || `file-alt`
  ,
};

export default {
  namespaced, state, mutations, actions, getters,
};
